import { useEffect, useRef } from 'react';

/**
 * צליל חיוג (ringback) בזמן שסבב מצלצל ללקוחות ועדיין אין אף אחד על הקו.
 *
 * בסבב מקבילי השיחה יוצאת מהשרת ללקוח, והסופטפון של הנציג שקט עד שמישהו
 * עונה — הנציג רואה "מצלצל" על המסך אבל לא שומע כלום, ולא ברור לו אם
 * המערכת בכלל מחייגת. הצליל הזה הוא החיווי: קצב ringback ישראלי (טון 425Hz,
 * שנייה צליל ושלוש שקט), עדין יותר מרינגטון של שיחה נכנסת כדי שלא יתבלבל
 * איתו — כשלקוח עונה הסופטפון מצלצל בצליל שלו, וזה נפסק.
 *
 * Web Audio ולא קובץ, כמו useRingtone: אין נכס להביא, ודורש מחווה קודמת של
 * המשתמש (הלחיצה על "הפעלת חייגן אוטומטי").
 */
export const RINGBACK_TONE = { freqHz: 425, onSec: 1.0, periodMs: 4000, gain: 0.07 };

export function useRingbackTone(active: boolean) {
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;

    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;

    let ctx: AudioContext;
    try {
      ctx = new AudioCtx();
    } catch {
      return;
    }
    void ctx.resume().catch(() => {});

    const burst = (start: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = RINGBACK_TONE.freqHz;
      const g = RINGBACK_TONE.gain;
      const dur = RINGBACK_TONE.onSec;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(g, start + 0.03);
      gain.gain.setValueAtTime(g, start + dur - 0.05);
      gain.gain.linearRampToValueAtTime(0, start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + dur);
    };

    const schedule = () => burst(ctx.currentTime + 0.05);
    schedule();
    timerRef.current = window.setInterval(schedule, RINGBACK_TONE.periodMs);

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
      ctx.close().catch(() => {});
    };
  }, [active]);
}
